import React from 'react';
import { Alert, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { styles } from '../posShared.styles';

type PosCartHeaderProps = {
  itemCountLabel: string;
  cartBadgePulse: boolean;
  cartLength: number;
  isCompactCart: boolean;
  canEdit: boolean;
  canParkCurrentSale: boolean;
  hasParkedSale: boolean;
  onParkCurrentSale?: () => void;
  selectedDiningTableName?: string | null;
  onChangeDiningTable?: () => void;
  inSheet?: boolean;
  /** Desk: título en banda counter alineada a la grilla (sin padding superior extra). */
  deskChrome?: boolean;
  /** Desk: fila del buscador (solo kicker + contador, sin borde inferior). */
  toolbarRow?: boolean;
};

export function CartClearButton({ onPress, sheet = false }: { onPress: () => void; sheet?: boolean }) {
  return (
    <TouchableOpacity
      testID="pos-clear-cart"
      accessibilityRole="button"
      accessibilityLabel="Vaciar carrito"
      style={[styles.cartHeaderParkBtn, sheet && styles.cartHeaderParkBtnSheet]}
      onPress={onPress}
    >
      <Ionicons name="trash-outline" size={15} color={ticketColors.ink2} />
      <Text style={[styles.cartHeaderParkBtnText, sheet && styles.cartHeaderParkBtnTextSheet]}>Vaciar carrito</Text>
    </TouchableOpacity>
  );
}

export function CartParkButton({ onPress, sheet = false }: { onPress: () => void; sheet?: boolean }) {
  return (
    <TouchableOpacity
      testID="pos-park-sale"
      accessibilityRole="button"
      accessibilityLabel="Pausar venta"
      style={[styles.cartHeaderParkBtn, sheet && styles.cartHeaderParkBtnSheet]}
      onPress={onPress}
    >
      <Ionicons
        name={sheet ? 'pause-outline' : 'pause-outline'}
        size={sheet ? 15 : 15}
        color={ticketColors.ink2}
      />
      <Text style={[styles.cartHeaderParkBtnText, sheet && styles.cartHeaderParkBtnTextSheet]}>Pausar venta</Text>
    </TouchableOpacity>
  );
}

export function PosCartHeader({
  itemCountLabel,
  cartBadgePulse,
  cartLength,
  isCompactCart,
  canEdit,
  canParkCurrentSale,
  hasParkedSale,
  onParkCurrentSale,
  selectedDiningTableName,
  onChangeDiningTable,
  inSheet = false,
  deskChrome = false,
  toolbarRow = false,
}: PosCartHeaderProps) {
  if (inSheet) {
    return null;
  }
  return (
    <View
      style={[
        styles.posCanvasCardHeader,
        deskChrome && styles.posCanvasCardHeaderDeskChrome,
        toolbarRow && styles.posCanvasCardHeaderToolbarRow,
      ]}
    >
      <View style={styles.cartHeaderTitleRow}>
        <View>
          <Text style={cartHeaderStyles.kicker}>Carrito</Text>
          {cartLength > 0 ? (
            <Text style={[cartHeaderStyles.title, isCompactCart && styles.cartTitleCompact]}>
              {itemCountLabel}
            </Text>
          ) : null}
        </View>
        {cartBadgePulse ? (
          <View style={[styles.cartItemCountBadge, styles.cartItemCountBadgePulse]}>
            <Text style={styles.cartItemCountBadgeText}>nuevo</Text>
          </View>
        ) : null}
        {selectedDiningTableName ? (
          <TouchableOpacity
            onPress={onChangeDiningTable}
            disabled={!onChangeDiningTable}
            accessibilityRole="button"
            accessibilityLabel="Cambiar mesa"
          >
            <Text style={styles.cartHeaderDiningTable} numberOfLines={1}>
              Mesa: {selectedDiningTableName}
              {onChangeDiningTable ? ' · Cambiar' : ''}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
      {!toolbarRow && canEdit && canParkCurrentSale && !hasParkedSale ? (
        <TouchableOpacity
          testID="pos-park-sale"
          accessibilityRole="button"
          accessibilityLabel="Pausar venta"
          style={styles.cartHeaderParkBtn}
          onPress={onParkCurrentSale ?? (() => {})}
        >
          <Ionicons name="pause-outline" size={15} color={ticketColors.ink2} />
          <Text style={styles.cartHeaderParkBtnText}>Pausar venta</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

const cartHeaderStyles = StyleSheet.create({
  kicker: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  title: {
    marginTop: 2,
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: -0.3,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
});
